import { useCallback, useState } from "react";
import { Schedule } from "../../../models/Schedule";
import { AssessmentSchedule } from "../../../models/Assessment";
import ScheduleRepository from "../../../repositories/ScheduleRepository";
import ScheduleTimeRepository from "../../../repositories/ScheduleTImeRepository";
import AssessmentRepository from "../../../repositories/AssessmentRepository";
import { handleErrorLoadDataResponse } from "../../../@vendor/utils";
import { useTranslation } from "react-i18next";
import { useIsMounted } from "usehooks-ts";

type GetScheduleParams = {
  assessment_id?: number | string;
  psychologist_id?: number | string;
  type?: string;
};

export const useGetAssessmentSchedule = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [assessmentSchedules, setAssessmentSchedules] = useState<AssessmentSchedule[]>([]);
  const [sessionScheduleTimes, setSessionScheduleTimes] = useState<Record<number, any[]>>({});
  const [fetchingSessionScheduleTimeIdx, setFetchingSessionScheduleTimeIdx] = useState<number>();

  const [isFetching, setIsFetching] = useState<boolean>(false);

  const loadData = useCallback(
    async (params: GetScheduleParams = {}) => {
      setIsFetching(true);
      setSchedules([]);
      setAssessmentSchedules([]);
      setSessionScheduleTimes({});

      try {
        const [scheduleRes, assessmentScheduleRes] = await Promise.all([
          ScheduleRepository.findAll({
            psychologist: params.psychologist_id,
            type: params.type,
            session_type: "ASSESSMENT",
          }),
          AssessmentRepository.getSchedule(params.assessment_id as number, {
            psychologist: params.psychologist_id,
            type: params.type,
          }),
        ]);
        setSchedules(scheduleRes.data.data);
        setAssessmentSchedules(assessmentScheduleRes.data.data);
      } catch (error: any) {
        if (error?.code !== "ERR_CANCELED") {
          handleErrorLoadDataResponse(t, t("common.text.availableSchedule"));
        }
      } finally {
        setIsFetching(false);
      }
    },
    [t]
  );

  const loadSessionScheduleTime = useCallback(
    async (idx: number, date: string, duration: number, psychologistId?: number, type?: string) => {
      setFetchingSessionScheduleTimeIdx(idx);

      try {
        const response = await ScheduleTimeRepository.findAll({
          psychologist_id: psychologistId,
          session_type: "ASSESSMENT",
          type: type,
          date,
          duration,
        });

        if (isMounted()) {
          const { data: responseData } = response.data;
          setSessionScheduleTimes((s) => ({
            ...s,
            [idx]: responseData,
          }));
          setFetchingSessionScheduleTimeIdx(undefined);
        }
      } catch {
        if (isMounted()) {
          setFetchingSessionScheduleTimeIdx(undefined);
        }
      }
    },
    [isMounted]
  );

  return {
    schedules,
    assessmentSchedules,
    sessionScheduleTimes,
    fetchingSessionScheduleTimeIdx,
    isFetching,
    loadData,
    loadSessionScheduleTime,
  };
};
